import React, { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import * as Styled from "./styles";
import {
  SmallTitle,
  GOLabel,
  GODropbox,
  GOButton,
  GOLayout
} from "../../../components";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { phoneNumberFormatting } from "../../../middlewares";
import GOEmptyText from "../../common/GOEmptyText";
import ReservationCountTable from "../ReservationCountTable";
import { employeeDetailTypes } from "../../../Types/employeeType";

import ReactPaginate from "react-paginate";
import { makeYearDataForComboBox } from "../../../util/converter";

const Detail = ({
  employeeDetailData,
  userListData,
  onMonthChanged,
  queryMonth,
  onFilterChanged,
  filter,
  attendanceCount,
  reservationCount,
  absenceCount,
  queryYear,
  onYearChanged
}: employeeDetailTypes) => {
  const { t } = useTranslation();
  const employeeLessonData = employeeDetailData.workTimes;
  const lessonInterval = employeeDetailData.lessonInterval;

  const yearComboData = makeYearDataForComboBox();

  const pageSize = 50;
  const [pagedData, setPagedData] = useState<any>([]);
  const [pageCount, setPageCount] = useState<number>(
    userListData.length / pageSize
  );
  const [currentPage, setCurrentPage] = useState<number>(0);

  useEffect(() => {
    setPagedData(userListData.slice(0, pageSize));
    setPageCount(userListData.length / pageSize);
    setCurrentPage(0);
  }, [userListData]);

  const onPageChange = (data: any) => {
    // setCurrentPageIndex(data.selected);
    setCurrentPage(data.selected);
    setPagedData(
      userListData.slice(
        data.selected * pageSize,
        data.selected * pageSize + pageSize
      )
    );
  };

  const formatPhoneNumber = (phoneNumberString: string) => {
    if (phoneNumberString.length > 1) {
      return (
        "" +
        phoneNumberString.slice(0, 3) +
        "-" +
        phoneNumberString.slice(3, 7) +
        "-" +
        phoneNumberString.slice(7, phoneNumberString.length)
      );
    }
    return null;
  };

  const formatStatus = (status: number) => {
    if (status === 0) {
      return t("components.employee.reservationComplete");
    } else if (status === 1) {
      return t("components.employee.attendant");
    } else if (status === 2) {
      return t("components.employee.absent");
    } else {
      return "";
    }
  };

  const excelData = userListData.map((user: any) => {
    return {
      name: user.userName,
      phoneNumber: formatPhoneNumber(user.phoneNumber),
      reservationDate: `${dayjs(user.startDate).format(
        "YYYY-MM-DD a h:mm"
      )} ~ ${dayjs(user.endDate).format("a h:mm")}`,
      ticketName: user.ticketName,
      status: formatStatus(user.status),
      lessonPro: employeeDetailData?.employee
        ? employeeDetailData?.employee.name
        : ""
    };
  });

  const lessonData = employeeLessonData?.slice().sort((a: any, b: any) => {
    return a["day"] - b["day"];
  });

  const days = [1, 2, 3, 4, 5, 6, 0];

  const birthday = (birth: any) => {
    const today = new Date().getFullYear();
    const myBirth = new Date(birth).getFullYear();
    return today - myBirth;
  };

  const viewDay = (data: any) => {
    if (data === 1) return `${t("components.employee.monday")}`;
    if (data === 2) return `${t("components.employee.tuesday")}`;
    if (data === 3) return `${t("components.employee.wednesday")}`;
    if (data === 4) return `${t("components.employee.thursday")}`;
    if (data === 5) return `${t("components.employee.friday")}`;
    if (data === 6) return `${t("components.employee.saturday")}`;
    if (data === 0) return `${t("components.employee.sunday")}`;
  };

  const viewWorkStartTime = (data: any) => {
    let name = "";
    let hour = "";
    let minute = "";

    if (data.startHour > 11) name = t("components.employee.pm");
    else name = t("components.employee.am");
    if (data.startHour === 24) name = t("components.employee.am");

    if (data.startHour < 10) {
      hour = `0${data.startHour}`;
    } else if (data.startHour > 10 && data.startHour < 13) {
      hour = `${data.startHour}`;
    } else if (data.startHour > 12 && data.startHour < 22) {
      hour = `0${data.startHour - 12}`;
    } else if (data.startHour > 21) {
      hour = `${data.startHour - 12}`;
    }
    if (data.startHour === 24) {
      hour = `0${data.startHour - 24}`;
    }
    if (data.startHour === 10) hour = `${data.startHour}`;

    // 분 표시
    if (data.startMinute < 10) minute = `0${data.startMinute}`;
    else minute = data.startMinute;

    return `${name} ${hour}:${minute}`;
  };

  const viewWorkEndTime = (data: any) => {
    let name = "";
    let hour = "";
    let minute = "";

    // 오전, 오후 조건
    if (data.endHour > 11) name = t("components.employee.pm");
    else name = t("components.employee.am");
    if (data.endHour === 24) name = t("components.employee.am");

    if (data.endHour < 10) {
      hour = `0${data.endHour}`;
    } else if (data.endHour > 10 && data.endHour < 13) {
      hour = `${data.endHour}`;
    } else if (data.endHour > 12 && data.endHour < 22) {
      hour = `0${data.endHour - 12}`;
    } else if (data.endHour > 21) {
      hour = `${data.endHour - 12}`;
    }
    if (data.endHour === 24) {
      hour = `0${data.endHour - 24}`;
    }
    if (data.endHour === 10) hour = `${data.endHour}`;

    if (data.endMinute < 10) minute = `0${data.endMinute}`;
    else minute = data.endMinute;

    return `${name} ${hour}:${minute}`;
  };

  return (
    <>
      <GOLayout
        body={
          <>
            <Styled.LayoutWrapper>
              {employeeDetailData && (
                <>
                  <Styled.Box>
                    <SmallTitle body={t("employee.info")} />
                    <Styled.UserDetailProfile>
                      <Styled.Text>
                        <h4>{employeeDetailData?.employee?.name}</h4>
                        <h6>
                          {
                            employeeDetailData?.employee?.dateOfBirth.split(
                              "T"
                            )[0]
                          }
                        </h6>
                        <h5>
                          {birthday(employeeDetailData?.employee?.dateOfBirth)}
                          세 /{" "}
                          {employeeDetailData?.employee?.sex === 0
                            ? t("common.male")
                            : t("common.female")}
                        </h5>
                      </Styled.Text>
                    </Styled.UserDetailProfile>
                    <hr />
                    <GOLabel
                      label={t("common.address")}
                      body={
                        employeeDetailData?.address
                          ? employeeDetailData?.address?.roadAddress
                          : "-"
                      }
                    />
                    <GOLabel
                      label={t("common.phoneNumber")}
                      body={phoneNumberFormatting(
                        employeeDetailData?.employee?.phoneNumber
                      )}
                    />
                    <hr />
                    <GOLabel
                      label={t("components.employee.job")}
                      body={
                        employeeDetailData?.employee?.type === 0
                          ? t("components.employee.employeeTypeAdmin")
                          : t("components.employee.employeeTypeManager") &&
                            employeeDetailData?.employee?.type === 2
                          ? t("components.employee.employeeTypeTeacher")
                          : t("components.employee.employeeTypeManager")
                      }
                    />
                    {lessonInterval &&
                      employeeDetailData?.employee?.type === 2 &&
                      lessonInterval > 0 && (
                        <>
                          <GOLabel
                            label={t("components.employee.lessonTime")}
                            body={`${lessonInterval}${t(
                              "components.employee.min"
                            )}`}
                          />
                        </>
                      )}
                    <br />
                  </Styled.Box>
                  <Styled.Box>
                    <SmallTitle body={t("employee.schedule")} />
                    {days.map((d: any, index: number) => (
                      <React.Fragment key={index}>
                        {lessonData[d].day === d && (
                          <>
                            <GOLabel
                              label={`${viewDay(d)}`}
                              body={
                                lessonData[d].day === d &&
                                lessonData[d]?.startHour === null &&
                                lessonData[d]?.startMinute === null
                                  ? "-"
                                  : `${viewWorkStartTime(lessonData[d])} 
                   ~ ${viewWorkEndTime(lessonData[d])}`
                              }
                            />
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </Styled.Box>
                  <Styled.Box>
                    <SmallTitle body={t("employee.confirmLesson")} />
                    {employeeDetailData?.lessonLogs?.length > 0 ? (
                      <>
                        {employeeDetailData.lessonLogs.map(
                          (lessonLog: any, index: number) => (
                            <React.Fragment key={index}>
                              <Styled.ConfirmLessonPanel>
                                <Styled.Info>
                                  {lessonLog.user.name}
                                  {/*/회원*/}
                                  {t("components.employee.member")}
                                </Styled.Info>
                                <Styled.Start />
                                <Styled.End>
                                  <div className="label">
                                    {t("components.employee.writtenDay")}
                                  </div>
                                  <div>
                                    {dayjs(lessonLog.createdAt).format(
                                      "YYYY-MM-DD"
                                    )}{" "}
                                    /
                                  </div>
                                  <div>
                                    {dayjs(lessonLog.createdAt).format("HH:mm")}
                                  </div>
                                </Styled.End>
                              </Styled.ConfirmLessonPanel>
                            </React.Fragment>
                          )
                        )}
                      </>
                    ) : (
                      <>
                        <GOEmptyText
                          title={t("components.employee.lessonPost")}
                        />
                      </>
                    )}
                  </Styled.Box>
                </>
              )}
            </Styled.LayoutWrapper>
            {employeeDetailData.employee.type === 2 && (
              <Styled.LayoutWrapper>
                <Styled.Box fullWidth>
                  <SmallTitle
                    body={`[${queryYear ? queryYear : "2022"}-${
                      queryMonth ? queryMonth + 1 : 1
                    } ${t("components.employee.lessonMemberListAndCount")}: 
                    ${userListData?.length}, ${t(
                      "components.employee.attendanceCount"
                    )} : ${attendanceCount ||
                      t("components.employee.nodata")}, ${t(
                      "components.employee.reservationCount"
                    )}  ${reservationCount ||
                      t("components.employee.nodata")}, ${t(
                      "components.employee.absenceCount"
                    )}  ${absenceCount || t("components.employee.nodata")}  ]`}
                  />

                  <Styled.Checkbox>
                    <GODropbox
                      data={yearComboData}
                      value={queryYear}
                      onChange={(e: any) => {
                        onYearChanged && onYearChanged(Number(e.target.value));
                      }}
                      xsmall
                    />
                    <GODropbox
                      data={[
                        { body: t("components.employee.january"), value: 0 },
                        { body: t("components.employee.february"), value: 1 },
                        { body: t("components.employee.march"), value: 2 },
                        { body: t("components.employee.april"), value: 3 },
                        { body: t("components.employee.may"), value: 4 },
                        { body: t("components.employee.june"), value: 5 },
                        { body: t("components.employee.july"), value: 6 },
                        { body: t("components.employee.august"), value: 7 },
                        { body: t("components.employee.september"), value: 8 },
                        { body: t("components.employee.october"), value: 9 },
                        { body: t("components.employee.november"), value: 10 },
                        { body: t("components.employee.december"), value: 11 }
                      ]}
                      value={queryMonth}
                      onChange={(e: any) => {
                        onMonthChanged &&
                          onMonthChanged(Number(e.target.value));
                      }}
                      xsmall
                    />
                    <div className="checkbox_group">
                      <div className="inputWrapper">
                        <input
                          type="checkbox"
                          name="total"
                          onChange={onFilterChanged}
                          checked={filter?.total}
                        />
                        <label className="checkLabel">
                          {t("components.employee.all")}
                        </label>
                      </div>
                      <div className="inputWrapper">
                        <input
                          type="checkbox"
                          name="attendance"
                          onChange={onFilterChanged}
                          checked={filter?.attendance}
                        />
                        <label className="checkLabel">
                          {t("components.employee.attendance")}
                        </label>
                      </div>
                      <div className="inputWrapper">
                        <input
                          type="checkbox"
                          name="reservation"
                          onChange={onFilterChanged}
                          checked={filter?.reservation}
                        />
                        <label className="checkLabel">
                          {t("components.employee.onReservation")}
                        </label>
                      </div>
                      <div className="inputWrapper">
                        <input
                          type="checkbox"
                          name="absence"
                          onChange={onFilterChanged}
                          checked={filter?.absence}
                        />
                        <label className="checkLabel">
                          {t("components.employee.absence")}
                        </label>
                      </div>
                    </div>
                    {!(
                      filter?.attendance ||
                      filter?.absence ||
                      filter?.reservation ||
                      filter?.total
                    ) ? (
                      <> </>
                    ) : (
                      <CSVLink
                        headers={[
                          { label: t("components.employee.name"), key: "name" },
                          {
                            label: t("components.employee.phone"),
                            key: "phoneNumber"
                          },
                          {
                            label: t("components.employee.reservationDay"),
                            key: "reservationDate"
                          },
                          {
                            label: t("components.employee.productName"),
                            key: "ticketName"
                          },
                          {
                            label: t("components.employee.status"),
                            key: "status"
                          },
                          {
                            label: t("components.employee.proName"),
                            key: "lessonPro"
                          }
                        ]}
                        data={excelData}
                        filename={`${queryMonth ? queryMonth + 1 : 1} ${t(
                          "components.employee.reservationListFileName"
                        )}`}
                        target="_blank"
                      >
                        <GOButton
                          body={t(
                            "components.employee.reservationListFileName"
                          )}
                          onClick={() => {}}
                        />
                      </CSVLink>
                    )}
                  </Styled.Checkbox>
                  <ReservationCountTable
                    data={pagedData}
                    startPageNumber={pageSize * currentPage}
                  />
                  <ReactPaginate
                    pageRangeDisplayed={10}
                    marginPagesDisplayed={5}
                    previousLabel={t("components.employee.previous")}
                    nextLabel={t("components.employee.next")}
                    pageCount={pageCount}
                    onPageChange={onPageChange}
                    containerClassName={"pagination"}
                    previousLinkClassName={"pagination__link"}
                    nextLinkClassName={"pagination__link"}
                    disabledClassName={"pagination__link--disabled"}
                    activeClassName={"pagination__link--active"}
                  />
                </Styled.Box>
              </Styled.LayoutWrapper>
            )}
          </>
        }
      />
    </>
  );
};

export default Detail;
